import { getNotificationPublishState } from '../../../models';
import { SortOrder } from 'react-base-table';
import _orderBy from 'lodash/orderBy';
import { RecipientsFilter } from '../../../../generated/apollo-hooks';
import { AppTableFilter, IAppTableRowData } from '../model';

interface IExtraCompareFunctionParams {
    environmentId?: string;
}

export const omitTopFixed = (rowData: $TSFixMe) => !rowData?.fixedTop;

export function sort({
    data,
    term,
    direction,
    environmentId,
    filters
}: {
    data?: IAppTableRowData[];
    term: string;
    direction: SortOrder;
    environmentId: string;
    filters: Record<string, string[]>;
}): IAppTableRowData[] {
    const sortableRows = data ? [...data.filter(row => !row.fixedTop)] : [];
    const fixedTopRows = data ? [...data.filter(row => row.fixedTop)] : [];

    const compareFunctions: {
        [key: string]: ((a: $TSFixMe, b: $TSFixMe, extraCompareFunctionParams?: IExtraCompareFunctionParams) => number) | null;
    } = {
        // @ts-expect-error ts-migrate(2322) FIXME: Type '(a: $TSFixMe, b: $TSFixMe, extraCompareFunct... Remove this comment to see the full error message
        dates: datesCompareFunction,
        engagement: engagementCompareFunction,
        subject: subjectCompareFunction,
        sender: senderCompareFunction,
        user: senderCompareFunction,
        segments: segmentsCompareFunction,
        audiences: audiencesCompareFunction,
        role: roleCompareFunction,
        size: getSizeCompareFunction(sortableRows, filters)
    };

    const compareFunction = compareFunctions[term];

    if (!compareFunction) {
        return fixedTopRows.concat(_orderBy(sortableRows, [term], [direction]));
    }

    const sortMultiply = direction === 'desc' ? -1 : 1;
    const extraCompareFunctionParams = { environmentId };
    const sorted = sortableRows.sort((a: $TSFixMe, b: $TSFixMe) => {
        return compareFunction(a, b, extraCompareFunctionParams) * sortMultiply;
    });
    return fixedTopRows.concat(sorted);
}

const compare = (a: $TSFixMe, b: $TSFixMe) => {
    if (a === undefined || b === undefined) return 0;
    if (a >= b) return 1;
    if (a < b) return -1;
    return 0;
};

const datesCompareFunction = (a: $TSFixMe, b: $TSFixMe, extraCompareFunctionParams: IExtraCompareFunctionParams) => {
    return compare(
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        getNotificationPublishState(a, extraCompareFunctionParams.environmentId).startAt,
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        getNotificationPublishState(b, extraCompareFunctionParams.environmentId).startAt
    );
};

const engagementCompareFunction = (a: $TSFixMe, b: $TSFixMe) => {
    return compare(a.analytics?.clicks || 0, b.analytics?.clicks || 0);
};

const subjectCompareFunction = (a: $TSFixMe, b: $TSFixMe) => {
    return compare(a?.title?.toLocaleLowerCase(), b.title?.toLocaleLowerCase());
};

const senderCompareFunction = (a: $TSFixMe, b: $TSFixMe) => {
    return compare(a.user.label, b.user.label);
};

const roleCompareFunction = (a: $TSFixMe, b: $TSFixMe) => {
    return compare(a.role.displayName, b.role.displayName);
};

const segmentsCompareFunction = (a: $TSFixMe, b: $TSFixMe) => {
    if (!a.targetAudience?.length && !a.audiencesIds?.length) {
        return -1;
    }

    if (!b.targetAudience?.length && !b.audiencesIds?.length) {
        return 1;
    }

    if (!a.targetAudience?.length && a.audiencesIds?.length) {
        return -1;
    }

    if (!b.targetAudience?.length && b.audiencesIds?.length) {
        return 1;
    }
    return compare(a.targetAudience?.length || 0, b.targetAudience?.length || 0);
};

const audiencesCompareFunction = (a: $TSFixMe, b: $TSFixMe) => {
    if (a?.endUsersIds?.length) {
        return -1;
    }

    if (b?.endUsersIds?.length) {
        return 1;
    }

    if (!a.audiences?.length && !a.targetAudience?.length) {
        return -1;
    }

    if (!b.audiences?.length && !b.targetAudience?.length) {
        return 1;
    }

    if (!a.audiences?.length && a.targetAudience?.length) {
        return -1;
    }

    if (!b.audiences?.length && b.targetAudience?.length) {
        return 1;
    }

    return compare(a.audiences?.length || 0, b.audiences?.length || 0);
};

interface IAudienceTableRowData extends IAppTableRowData {
    size: number;
    sizeTotal: number;
    sizeInactive: number;
    [key: string]: unknown;
}

const hasRequiredProps = (data: IAppTableRowData[]): data is IAudienceTableRowData[] => {
    return !!data.length && data[0] && 'size' in data[0] && 'sizeTotal' in data[0];
};

const getSizeCompareFunction = (data: IAppTableRowData[], filters?: Record<string, string[]>) => {
    if (hasRequiredProps(data)) {
        return (a: IAudienceTableRowData, b: IAudienceTableRowData) => {
            const selectedDataSourceValue = filters?.['size']?.[0];
            switch (selectedDataSourceValue) {
                case RecipientsFilter.All:
                    return compare(a.sizeTotal, b.sizeTotal);
                case RecipientsFilter.Inactive:
                    return compare(a.sizeInactive, b.sizeInactive);
                case RecipientsFilter.Potential:
                    return compare(a.sizeTotal - a.size - a.sizeInactive, b.sizeTotal - b.size - b.sizeInactive);
                default:
                    return compare(a.size, b.size);
            }
        };
    }
    return null;
};

const defaultExecFilter = (rowField: string | string[], filtersList: Array<string | undefined>) => {
    if (Array.isArray(rowField)) {
        return filtersList.some(filterField => filterField && rowField.includes(filterField));
    }

    return filtersList.includes(rowField?.toString());
};

export const filterRows = (rows: IAppTableRowData[], filtersList: { [key: string]: AppTableFilter[] }, columns: $TSFixMe[]): typeof rows => {
    return rows.filter(row => {
        if (row.fixedTop) {
            return true;
        }

        const allExecFilters = columns
            .filter(column => column?.filterMetaData?.active)
            .map(column => {
                const checkedList = (filtersList?.[column.dataKey]?.filter?.(({ checked }) => checked).map(({ value }) => value) || []).map(str =>
                    str?.toString()
                );

                return checkedList.length
                    ? column?.filterMetaData?.execFilter
                        ? column?.filterMetaData?.execFilter(row, checkedList)
                        : defaultExecFilter(row[column.dataKey], checkedList)
                    : true;
            });

        return allExecFilters.every(Boolean);
    });
};
